import { lazy, Suspense, ComponentType } from "react";

// Retry lazy imports once on failure (handles stale Vite chunks after deploys/HMR).
function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      const key = "lcs_chunk_reload";
      if (typeof window !== "undefined" && !sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        window.location.reload();
        // Return a never-resolving promise while reload happens
        return await new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}

// Toaster + Sonner lazy-loaded so they don't bloat the initial JS parse.
const Toaster = lazyWithRetry(() => import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })));
const Sonner = lazyWithRetry(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AdminRoute } from "./components/AdminRoute";
import { SubscriptionGate } from "./components/SubscriptionGate";

// Wraps a learner route in ProtectedRoute + SubscriptionGate.
// Profile, Welcome, CheckoutReturn, Auth, and Admin routes are intentionally excluded.
const Gated = ({ children }: { children: React.ReactNode }) => (
  <ProtectedRoute>
    <SubscriptionGate>{children}</SubscriptionGate>
  </ProtectedRoute>
);

// Eagerly loaded entry routes (small + common landing points)
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";

// Route-level code splitting — each chunk loads on demand,
// breaking the single large main-thread task into smaller ones.
const Resources = lazy(() => import("./pages/Resources"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Profile = lazy(() => import("./pages/Profile"));
const AccentChallengeCourse = lazy(() => import("./pages/AccentChallengeCourse"));
const ConjugationTrainer = lazy(() => import("./pages/ConjugationTrainer"));
const EssentialVerbsAssessment = lazy(() => import("./pages/EssentialVerbsAssessment"));
const ConjugationConfig = lazy(() => import("./pages/ConjugationConfig"));
const FlashcardReview = lazy(() => import("./components/FlashcardReview"));
const PronunciationRecorder = lazy(() => import("./pages/PronunciationRecorder"));
const PhoneticsCorrection = lazy(() => import("./pages/PhoneticsCorrection"));
const RecordingsLibrary = lazy(() => import("./pages/RecordingsLibrary"));
const BlueprintCourse = lazy(() => import("./pages/BlueprintCourse"));
const BlueprintSentenceBuilder = lazy(() => import("./pages/BlueprintSentenceBuilder"));
const BlueprintSentenceBuilder1 = lazy(() => import("./pages/BlueprintSentenceBuilder1"));
const BlueprintSentenceBuilder2 = lazy(() => import("./pages/BlueprintSentenceBuilder2"));
const BlueprintSentenceBuilder3 = lazy(() => import("./pages/BlueprintSentenceBuilder3"));
const BlueprintSentenceBuilder4 = lazy(() => import("./pages/BlueprintSentenceBuilder4"));
const BlueprintSentenceBuilder5 = lazy(() => import("./pages/BlueprintSentenceBuilder5"));
const BlueprintSentenceBuilder6 = lazy(() => import("./pages/BlueprintSentenceBuilder6"));
const BlueprintSentenceBuilder7 = lazy(() => import("./pages/BlueprintSentenceBuilder7"));

const Admin = lazy(() => import("./pages/Admin"));
const AdminMysteryCallForm = lazy(() => import("./pages/AdminMysteryCallForm"));
const StudentDashboard = lazy(() => import("./pages/StudentDashboard"));
const Workspace = lazy(() => import("./pages/Workspace"));
const PendingApproval = lazy(() => import("./pages/PendingApproval"));
const MysteryCallSchedule = lazy(() => import("./pages/MysteryCallSchedule"));
const MysteryCallsBookingCalendar = lazy(() => import("./pages/MysteryCallsBookingCalendar"));
const MysteryCallsIntro = lazy(() => import("./pages/MysteryCallsIntro"));
const MysteryCallSelfAssessment = lazy(() => import("./pages/MysteryCallSelfAssessment"));
const MysteryCallTutorAssessment = lazy(() => import("./pages/MysteryCallTutorAssessment"));
const MysteryCallTutorFeedback = lazy(() => import("./pages/MysteryCallTutorFeedback"));
const CoachingV2 = lazy(() => import("./pages/CoachingV2"));
const CoachingV3 = lazy(() => import("./pages/CoachingV3"));
const MyLessons = lazy(() => import("./pages/MyLessons"));
const InductionCourse = lazy(() => import("./pages/InductionCourse"));
const FluencyCourse = lazy(() => import("./pages/FluencyCourse"));
const Assessment = lazy(() => import("./pages/Assessment"));
const VocabAssessment = lazy(() => import("./pages/VocabAssessment"));
const ConversationPracticeV3 = lazy(() => import("./pages/ConversationPracticeV3"));
const ConversationHistory = lazy(() => import("./pages/ConversationHistory"));
const ConversationHistoryDetail = lazy(() => import("./pages/ConversationHistoryDetail"));
const Welcome = lazy(() => import("./pages/Welcome"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));
const TutorApplication = lazy(() => import("./pages/TutorApplication"));
const TutorTrainingHub = lazy(() => import("./pages/TutorTrainingHub"));
const TutorApprovalResult = lazy(() => import("./pages/TutorApprovalResult"));

const queryClient = new QueryClient();

// Invisible fallback — preserves layout, avoids any UX flash.
const RouteFallback = () => <div aria-hidden="true" style={{ minHeight: "100vh" }} />;

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Suspense fallback={null}>
        <Toaster />
        <Sonner />
      </Suspense>
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <AppErrorBoundary>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/pending-approval" element={<PendingApproval />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/resources" element={<Gated><Resources /></Gated>} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/accent-challenge" element={<Gated><AccentChallengeCourse /></Gated>} />
                <Route path="/verb-echo" element={<Gated><ConjugationTrainer /></Gated>} />
                <Route path="/verb-echo/essential-verbs" element={<Gated><EssentialVerbsAssessment /></Gated>} />
                <Route path="/verb-echo/config-preview" element={<Gated><ConjugationConfig /></Gated>} />
                <Route path="/flashcard-review" element={<Gated><FlashcardReview /></Gated>} />
                <Route path="/pronunciation-recorder" element={<Gated><PronunciationRecorder /></Gated>} />
                <Route path="/phonetics-correction" element={<Gated><PhoneticsCorrection /></Gated>} />
                <Route path="/recordings-library" element={<Gated><RecordingsLibrary /></Gated>} />
                <Route path="/blueprint" element={<Gated><BlueprintCourse /></Gated>} />
                <Route path="/blueprint/0" element={<Gated><BlueprintSentenceBuilder /></Gated>} />
                <Route path="/blueprint/1" element={<Gated><BlueprintSentenceBuilder1 /></Gated>} />
                <Route path="/blueprint/2" element={<Gated><BlueprintSentenceBuilder2 /></Gated>} />
                <Route path="/blueprint/3" element={<Gated><BlueprintSentenceBuilder3 /></Gated>} />
                <Route path="/blueprint/4" element={<Gated><BlueprintSentenceBuilder4 /></Gated>} />
                <Route path="/blueprint/5" element={<Gated><BlueprintSentenceBuilder5 /></Gated>} />
                <Route path="/blueprint/6" element={<Gated><BlueprintSentenceBuilder6 /></Gated>} />
                <Route path="/blueprint/7" element={<Gated><BlueprintSentenceBuilder7 /></Gated>} />
                <Route path="/conjugation-trainer" element={<Navigate to="/verb-echo" replace />} />
                <Route path="/conjugation-trainer/*" element={<Navigate to="/verb-echo" replace />} />
                <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
                <Route path="/admin/mystery-calls-calendar" element={<AdminRoute><MysteryCallsBookingCalendar /></AdminRoute>} />
                <Route path="/admin/mystery-call-form/:type/:id" element={<AdminRoute><AdminMysteryCallForm /></AdminRoute>} />
                <Route path="/dashboard" element={<Gated><StudentDashboard /></Gated>} />
                <Route path="/student-dashboard" element={<Navigate to="/dashboard" replace />} />
                <Route path="/workspace" element={<Gated><Workspace /></Gated>} />
                <Route path="/mystery-calls/schedule" element={<Gated><MysteryCallSchedule /></Gated>} />
                <Route path="/mystery-calls-intro" element={<Gated><MysteryCallsIntro /></Gated>} />
                <Route path="/mystery-calls/self-assessment" element={<Gated><MysteryCallSelfAssessment /></Gated>} />
                <Route path="/mystery-calls/tutor-assessment" element={<MysteryCallTutorAssessment />} />
                <Route path="/mystery-calls/tutor-feedback" element={<Gated><MysteryCallTutorFeedback /></Gated>} />
                <Route path="/coaching-v2" element={<Gated><CoachingV2 /></Gated>} />
                <Route path="/coaching-v3" element={<Gated><CoachingV3 /></Gated>} />
                <Route path="/mes-lecons" element={<Gated><MyLessons /></Gated>} />
                <Route path="/induction" element={<Gated><InductionCourse /></Gated>} />
                <Route path="/fluency-course" element={<Gated><FluencyCourse /></Gated>} />
                <Route path="/assessment" element={<Gated><Assessment /></Gated>} />
                <Route path="/vocab-assessment" element={<Gated><VocabAssessment /></Gated>} />
                <Route path="/conversation-practice-v3" element={<Gated><ConversationPracticeV3 /></Gated>} />
                <Route path="/conversation-history" element={<Gated><ConversationHistory /></Gated>} />
                <Route path="/conversation-history/:id" element={<Gated><ConversationHistoryDetail /></Gated>} />
                <Route path="/welcome" element={<Welcome />} />
                <Route path="/checkout/return" element={<CheckoutReturn />} />
                <Route path="/tutors/inscription" element={<TutorApplication />} />
                <Route path="/tutors/dossier-formation" element={<TutorTrainingHub />} />
                <Route path="/admin/tutor-approval-result" element={<TutorApprovalResult />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </AppErrorBoundary>
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
